import { ActionType } from 'typesafe-actions';
import {
    currentDate,
    getListOfNextStartingMonths,
    getNextStartingMonth,
    getNextStartingMonthDkLocale
} from '../lib/date';
import { IFormStore, ICoverage, ICoverages } from '../type';
import * as actionKeys from './action-keys';
import * as actions from './actions';

const DEFAULT_STATE: IFormStore = {
    model: null,
    coverageSettings: {
        selectedCoverages: []
    },
    currentInsurance: {
        accident: {
            shouldTerminate: null,
            terminationDate: '',
            currentInsuranceCompanyId: '',
            currentInsuranceCompanyName: '',
            currentInsuranceNumber: ''
        },
        contents: {
            shouldTerminate: null,
            terminationDate: '',
            currentInsuranceCompanyId: '',
            currentInsuranceCompanyName: '',
            currentInsuranceNumber: ''
        },
        newInsuranceStartDate: currentDate,
        showNewInsuranceStartDateInputType: null
    },
    customer: {
        name: '',
        cprNumber: '',
        address: '',
        mobile: '',
        email: '',
        skybrudIsChecked: false,
        skaderIsChecked: false,
        gaeldIsChecked: false
    },
    payment: {
        regNum: '',
        accountNum: '',
        isSomeoneElseAccount: false,
        accountHolderCprNumber: '',
        agreeTermsOfPurchase: false,
        agreeObtainInfo: false,
        isPaymentValid: true
    },
    meta: {
        currentDate,
        closestTerminationDate: getNextStartingMonth(2),
        closestTerminationDateDisplay: getNextStartingMonthDkLocale(2),
        terminationDateOptions: getListOfNextStartingMonths()
    }
};

type FormAction = ActionType<typeof actions>;

function getSelectedCoverage(coverage: ICoverage) {
    return {
        objectId: coverage.objectId,
        policyEnum: coverage.policyEnum,
        policyOfferId: coverage.policyOfferId,
        objectCoverageId: coverage.objectCoverageId
    };
}

function getCoverages(coverages: ICoverages) {
    const selectedCoverages = [];

    if (coverages.accident) {
        selectedCoverages.push(...coverages.accident.filter(coverage => coverage.chosen).map(getSelectedCoverage));
    }

    if (coverages.contents) {
        selectedCoverages.push(...coverages.contents.filter(coverage => coverage.chosen).map(getSelectedCoverage));
    }

    return selectedCoverages;
}

function setContentCoverages(coverages?: ICoverage[]) {
    if (!coverages) {
        return [];
    }

    const relatedCoverage = coverages.find(coverage => coverage.nameId === 'kortslutning-mm.');

    return coverages.map(coverage => {
        if (coverage.nameId === 'udvidet-elektronik' && relatedCoverage) {
            return {
                ...coverage,
                installmentPrice: coverage.installmentPrice + relatedCoverage.installmentPrice
            };
        }

        return coverage;
    });
}

export const formReducer = (state: IFormStore = DEFAULT_STATE, action: FormAction): IFormStore => {
    switch (action.type) {
        case actionKeys.setModel: {
            const { coverages } = action.payload;

            return {
                ...state,
                model: {
                    ...action.payload,
                    coverages: {
                        accident: coverages.accident ? [...coverages.accident] : [],
                        contents: setContentCoverages(coverages.contents)
                    }
                },
                coverageSettings: {
                    ...state.coverageSettings,
                    selectedCoverages: getCoverages(action.payload.coverages)
                }
            };
        }

        case actionKeys.updateModel:
            return {
                ...state,
                model: state.model
                    ? {
                          ...state.model,
                          offerNumber: action.payload.offerNumber,
                          dealPrice: action.payload.dealPrice,
                          disabilityCoverageAmount: action.payload.disabilityCoverageAmount
                      }
                    : null,
                coverageSettings: {
                    ...state.coverageSettings,
                    selectedCoverages: getCoverages(action.payload.coverages)
                }
            };

        case actionKeys.setInsuranceShouldTerminate:
            return {
                ...state,
                currentInsurance: {
                    ...state.currentInsurance,
                    [action.payload.insuranceType]: {
                        ...state.currentInsurance[action.payload.insuranceType],
                        shouldTerminate: action.payload.shouldTerminate,
                        terminationDate: action.payload.shouldTerminate ? state.meta.closestTerminationDate : '',
                        currentInsuranceCompanyId: '',
                        currentInsuranceCompanyName: ''
                    }
                }
            };

        case actionKeys.setShowNewInsuranceStartDateInputType:
            return {
                ...state,
                currentInsurance: {
                    ...state.currentInsurance,
                    showNewInsuranceStartDateInputType: action.payload.insuranceType
                }
            };

        case actionKeys.setInsuranceTerminateDate:
            return {
                ...state,
                currentInsurance: {
                    ...state.currentInsurance,
                    [action.payload.insuranceType]: {
                        ...state.currentInsurance[action.payload.insuranceType],
                        terminationDate: action.payload.terminationDate
                    }
                }
            };

        case actionKeys.setInsuranceCompany:
            return {
                ...state,
                currentInsurance: {
                    ...state.currentInsurance,
                    [action.payload.insuranceType]: {
                        ...state.currentInsurance[action.payload.insuranceType],
                        currentInsuranceCompanyId: action.payload.insuranceCompanyId,
                        currentInsuranceCompanyName: action.payload.insuranceCompanyName
                    }
                }
            };

        case actionKeys.setInsuranceNumber:
            return {
                ...state,
                currentInsurance: {
                    ...state.currentInsurance,
                    [action.payload.insuranceType]: {
                        ...state.currentInsurance[action.payload.insuranceType],
                        currentInsuranceNumber: action.payload.insuranceNumber
                    }
                }
            };

        case actionKeys.setNewInsuranceStartDate:
            return {
                ...state,
                currentInsurance: {
                    ...state.currentInsurance,
                    newInsuranceStartDate: action.payload
                }
            };

        case actionKeys.setCustomerName:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    name: action.payload
                }
            };

        case actionKeys.setCustomerCpr:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    cprNumber: action.payload
                }
            };
        case actionKeys.setCustomerAddress:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    address: action.payload
                }
            };
        case actionKeys.setCustomerMobile:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    mobile: action.payload
                }
            };
        case actionKeys.setCustomerEmail:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    email: action.payload
                }
            };

        case actionKeys.checkSkybrud:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    skybrudIsChecked: action.payload
                }
            };
        case actionKeys.checkSkader:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    skaderIsChecked: action.payload
                }
            };
        case actionKeys.checkGaeld:
            return {
                ...state,
                customer: {
                    ...state.customer,
                    gaeldIsChecked: action.payload
                }
            };

        case actionKeys.setRegNum:
            return {
                ...state,
                payment: {
                    ...state.payment,
                    regNum: action.payload,
                    isPaymentValid: true
                }
            };

        case actionKeys.setAccountNum:
            return {
                ...state,
                payment: {
                    ...state.payment,
                    accountNum: action.payload,
                    isPaymentValid: true
                }
            };

        case actionKeys.setIsSomeoneElseAccount:
            return {
                ...state,
                payment: {
                    ...state.payment,
                    isSomeoneElseAccount: action.payload
                }
            };

        case actionKeys.setAccountHolderCprNumber:
            return {
                ...state,
                payment: {
                    ...state.payment,
                    accountHolderCprNumber: action.payload
                }
            };

        case actionKeys.setAgreeTermsOfPurchase:
            return {
                ...state,
                payment: {
                    ...state.payment,
                    agreeTermsOfPurchase: action.payload
                }
            };

        case actionKeys.setAgreeObtainInfo:
            return {
                ...state,
                payment: {
                    ...state.payment,
                    agreeObtainInfo: action.payload
                }
            };

        case actionKeys.setIsPaymentValid:
            return {
                ...state,
                payment: {
                    ...state.payment,
                    isPaymentValid: false
                }
            };

        default:
            return state;
    }
};
